import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import CustomEase from 'gsap/CustomEase'
import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants.js'
import data from './assets/data.json'

gsap.registerPlugin(ScrollTrigger)
gsap.registerPlugin(CustomEase)

const SELECTORS = {
  pride: '.js-pride',
  bgVideo: '.js-pride__bg-video',
  header: '.js-pride__text-container',
  arrow: '.js-pride__arrow-down',
  mediaButton: '.js-pride__media-button',
  introText: '.js-pride__controller_intro',
  introTextFirst: '.js-pride__controller_intro-first',
  introTextSecond: '.js-pride__controller_intro-second',
  videoButtonPlayIcon: '.js-card-video-button-play-icon',
  videoButtonPauseIcon: '.js-card-video-button-pause-icon'
}

class Pride {
  constructor (element) {
    this.element = element
    this.bgVideo = this.element.querySelector(SELECTORS.bgVideo)
    this.header = this.element.querySelector(SELECTORS.header)
    this.arrow = this.element.querySelector(SELECTORS.arrow)
    this.videoButton = this.element.querySelector(SELECTORS.mediaButton)
    this.introText = this.element.querySelector(SELECTORS.introText)
    this.introTextFirst = this.element.querySelector(SELECTORS.introTextFirst)
    this.introTextSecond = this.element.querySelector(SELECTORS.introTextSecond)
    this.emitter = GLOBAL_CONSTANTS.UTILS.EMITTER
    this.data = null
    this.videoButtonPlayIcon = this.videoButton.querySelector(SELECTORS.videoButtonPlayIcon)
    this.videoButtonPauseIcon = this.videoButton.querySelector(SELECTORS.videoButtonPauseIcon)
    this.init()
    this.isVideoPlaying = null
  }

  init() {
    this.registerEvents()
    this.triggerIntroVideo()
    this.prepData()

    gsap.fromTo(this.element, { opacity: 0 }, { opacity: 1 })
    window.scrollTo(0, 0)

    if (
      this.bgVideo.paused === false &&
      this.bgVideo.ended === false
    ) {
      this.videoButton.style.display = ''
    }
  }

  registerEvents() {
    this.emitter.on(GLOBAL_CONSTANTS.EVENTS.SCROLL, this.scrollMethod.bind(this))
    this.bgVideo.addEventListener('ended', this.onVideoEnd.bind(this))

    this.bgVideo.oncanplay = () => {
      this.showPauseButton()
    }

    this.bgVideo.onplaying = () => {
      this.handleVideoPlaying()
    }

    this.bgVideo.onended = () => {
      this.handleVideoPaused()

      setTimeout(() => {
        this.videoButton.style.display = 'none'
      }, 600)
    }

    this.bgVideo.onpause = () => {
      this.handleVideoPaused()
    }

    this.videoButton.onclick = (event) => this.handleVideoButtonClick(event)
  }

  onVideoEnd() {
    CustomEase.create('bezierPride', '0.25,0.1,0.25,1.0')
    if (document.documentElement.classList.contains('reduced-motion')) return

    if (this.introText.getBoundingClientRect().top >= 100) {
      gsap.to(window, { duration: GLOBAL_CONSTANTS.TIMING.SLOWER_ANIM_TIME * 2 / 1000, scrollTo: SELECTORS.introText, ease: 'bezierPride' })
    }
  }

  showPauseButton() {
    this.videoButton.style.display = ''
  }

  triggerIntroVideo() {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: this.element,
        endTrigger: this.introText,
        start: 'top top',
        end: 'center center',
        scrub: 1
      }
    })

    tl.to([this.header, this.arrow, this.videoButton], {
      opacity: 0,
      y: '-10%'
    })
    tl.to(this.introText, { opacity: 1 })

    gsap.to(this.introTextFirst, {
      scrollTrigger: {
        trigger: this.introText,
        pin: true,
        start: 'top top',
        end: 'bottom +=100vh',
        scrub: 1,
        onUpdate: (self) => {
          if (self.progress >= 0.5) {
            gsap.to(this.introTextFirst, { opacity: 0 })
            gsap.to(this.introTextSecond, { opacity: 1 })
          } else {
            gsap.to(this.introTextFirst, { opacity: 1 })
            gsap.to(this.introTextSecond, { opacity: 0 })
          }
        }
      }
    })
  }

  scrollMethod() {
    if (window.scrollY > window.innerHeight / 2) {
      this.bgVideo.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)
    } else {
      this.bgVideo.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
    }
  }

  shuffleData(array) {
    return array.sort(() => 0.5 - Math.random())
  }

  prepData() {
    const randomizedData = this.shuffleData(data)

    randomizedData.forEach((item, index) => {
      if (item.name === 'Progress Pride') {
        randomizedData.splice(index, 1)
        randomizedData.unshift(item)
      }

      if (item.name === 'Ally') {
        randomizedData.splice(index, 1)
        randomizedData.push(item)
      }
    })

    this.data = randomizedData

    this.emitter.emit(GLOBAL_CONSTANTS.EVENTS.PRIDE_DATA_LOADED, this.data)
  }

  handleVideoButtonClick(event) {
    event.preventDefault()
    if (this.isVideoPlaying) {
      this.bgVideo.pause()
    } else {
      this.bgVideo.play()
    }
  }

  handleVideoPaused() {
    this.isVideoPlaying = false
    if (this.videoButton) {
      this.videoButtonPlayIcon.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
      this.videoButtonPauseIcon.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)
      this.videoButton.ariaLabel = 'play background animation'
    }
  }

  handleVideoPlaying() {
    this.isVideoPlaying = true
    if (this.videoButton) {
      this.videoButtonPlayIcon.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)
      this.videoButtonPauseIcon.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
      this.videoButton.ariaLabel = 'pause background animation'
    }
  }
}

export default {
  selector: SELECTORS.pride,
  Source: Pride
}
